import { Check, Clock } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { FadeUp, LineReveal } from "@/components/site/Reveal";
import { CtaBand } from "@/components/site/CtaBand";
import { IMG } from "@/lib/content";

const FACTS = [
  { big: "30%", label: "of battery system costs", text: "Covers households, businesses and community facilities. Despite the name, the program caters for almost all size business types." },
  { big: "5–100", label: "kWh nominal capacity", text: "Eligible systems must be between 5–100 kWh, with the rebate applying to the first 50 kWh of usable capacity." },
  { big: "$18.5K", label: "potential saving", text: "A 50 kWh battery installation could save a small business up to $18,500 with this rebate alone." },
];

const AVAILABLE = [
  "New solar + battery installations",
  "Upsizing your existing system by up to 200% — with additional major rebates to optimise feeding new and existing batteries",
  "Adding batteries and/or more panels to an existing solar system",
  "Upsizing an existing battery system",
  "Single battery units or modular systems functioning as one unit",
  "Stackable with eligible state-based battery incentives and feed-in tariffs (up to 8c currently)",
];

const FUNDING = [
  "Funded entirely from your greatly reduced or eliminated power bill savings",
  "No working capital or overdraft required at all",
  "Zero upfront costs — with major profit increases from day one",
  "Manageable, fully GST & tax deductible monthly payments",
  "On-balance sheet or off-balance sheet leasing — simply swap your power expense for a solar + battery payment",
  "Improved cash flow and net profits from day one for 25 years",
];

const List = ({ items, testId }) => (
  <ul data-testid={testId} className="space-y-3">
    {items.map((t) => (
      <li key={t} className="flex gap-3 items-start text-slate-200">
        <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[var(--sun)] text-[var(--ink)] grid place-items-center"><Check size={14} strokeWidth={3} /></span>
        {t}
      </li>
    ))}
  </ul>
);

export default function Batteries() {
  return (
    <div data-testid="batteries-page">
      <PageHero testId="batteries-hero" eyebrow="Cheaper Solar Batteries Program" lines={["30% off", "your batteries."]} intro="Launched 1 July 2025, the federal battery rebate is designed to supercharge Australia's battery installations to one million by 2030 — and it's built to slash, and in most cases eliminate, your power bills for good." image={IMG.battery} />
      <section className="bg-[var(--ink)] pb-24">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 grid md:grid-cols-3 gap-4">
          {FACTS.map((f, i) => (
            <FadeUp key={f.label} delay={i * 0.08} data-testid={`battery-fact-${i}`} className={`rounded-2xl p-8 ${i === 0 ? "bg-[var(--sun)] text-[var(--ink)]" : "bg-[var(--surface)] border border-white/10"}`}>
              <p className="font-display font-black text-7xl leading-none">{f.big}</p>
              <p className={`mt-2 font-mono text-xs tracking-[0.2em] uppercase ${i === 0 ? "" : "text-[var(--solar-light)]"}`}>{f.label}</p>
              <p className={`mt-5 leading-relaxed ${i === 0 ? "text-[var(--ink)]/80" : "text-slate-300"}`}>{f.text}</p>
            </FadeUp>
          ))}
        </div>
      </section>
      <section className="bg-[var(--navy)]">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-2 gap-14">
          <div>
            <p className="eyebrow">The rebate is available for</p>
            <LineReveal onView lines={["New, upsized", "or add-on."]} className="mt-5 font-display font-black uppercase leading-[0.88] text-5xl sm:text-6xl" />
            <FadeUp className="mt-10"><List items={AVAILABLE} testId="battery-available-list" /></FadeUp>
          </div>
          <div>
            <p className="eyebrow">Green Funding Payment Plan</p>
            <LineReveal onView lines={["Funded by your", "savings. Day one."]} className="mt-5 font-display font-black uppercase leading-[0.88] text-5xl sm:text-6xl" />
            <FadeUp className="mt-10"><List items={FUNDING} testId="battery-funding-list" /></FadeUp>
          </div>
        </div>
      </section>
      <section data-testid="battery-urgency" className="bg-[var(--concrete)] text-[var(--ink)]">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow eyebrow-dark"><Clock size={12} /> The 30% rebate won't last</p>
            <LineReveal onView lines={["Don't sleep", "on this one."]} className="mt-5 font-display font-black uppercase leading-[0.88] text-5xl sm:text-6xl lg:text-7xl" />
            <FadeUp><p className="mt-6 text-lg text-slate-700 max-w-xl">If you ever kicked yourself for missing out on solar during the heyday of feed-in tariffs — the rebate is set to substantially decline yearly until 2030. The time to act decisively is right now.</p></FadeUp>
          </div>
          <FadeUp delay={0.1} className="lg:col-span-5 rounded-2xl bg-[var(--ink)] text-white p-8 lg:p-10">
            <p className="font-display font-black text-7xl lg:text-8xl text-[var(--sun)] leading-none">$2.3B</p>
            <p className="mt-3 text-slate-300">of subsidies fully quoted by the solar industry in just <span className="text-white font-semibold">10 weeks</span>.</p>
          </FadeUp>
        </div>
      </section>
      <CtaBand />
    </div>
  );
}
